/* eslint-disable react/no-unescaped-entities */
/**
 * Listing Studio — landlord-facing smart features for property marketing.
 *
 *   • Virtual Staging — drop an empty room photo, pick a style, get a
 *     furnished version back. Manual download.
 *   • Spec Sheet      — fill a short form, get a polished one-page
 *     listing description. Per-section copy buttons + a "Copy all" cmd.
 *
 * Gated by ADMIN_CONFIG_TOKEN for now. Same token as ViralPost / Follower.
 */
import { useEffect, useRef, useState } from "react";
import {
  Sparkles, Loader2, Lock, Copy, Upload, Download, Image as ImageIcon,
  Wand2, FileText, ExternalLink, RotateCcw,
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const TOKEN_KEY = "cr_admin_token";

function cn(...c) { return c.filter(Boolean).join(" "); }

async function apiJson(path, { token, method = "GET", body } = {}) {
  const r = await fetch(`${API}/api/listing${path}`, {
    method,
    headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "X-Admin-Token": token, ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401) throw new Error("unauthorized");
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail || `Request failed (${r.status})`);
  }
  return r.json();
}

function LockScreen({ onUnlock }) {
  const [v, setV] = useState("");
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-5">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><Lock className="w-5 h-5 text-emerald-700"/></div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Listing Studio</h1>
            <p className="text-sm text-slate-500">Internal team only</p>
          </div>
        </div>
        <div className="mt-6 space-y-3">
          <label className="text-sm font-semibold text-slate-700">Admin token</label>
          <input type="password" value={v} onChange={(e) => setV(e.target.value)} data-testid="studio-token-input" placeholder="crc-cfg-…" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"/>
          <button data-testid="studio-unlock-btn" onClick={() => v.trim() && onUnlock(v.trim())} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2.5 rounded-lg text-sm">Unlock</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Virtual Staging tab
// ---------------------------------------------------------------------------
const STAGE_STYLES = [
  { key: "modern",  label: "Modern" },
  { key: "cozy",    label: "Cozy" },
  { key: "family",  label: "Family" },
  { key: "luxury",  label: "Luxury" },
  { key: "rental",  label: "Rental-ready" },
];

function StagingTab({ token }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [style, setStyle] = useState("rental");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [staged, setStaged] = useState(null);
  const inputRef = useRef(null);

  function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      setError("Photo must be JPEG, PNG, or WEBP"); return;
    }
    if (f.size > 6 * 1024 * 1024) {
      setError("Photo must be under 6 MB"); return;
    }
    setError("");
    setFile(f);
    setStaged(null);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  }

  async function generate() {
    if (!file || busy) return;
    setBusy(true); setError(""); setStaged(null);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("style", style);
      fd.append("notes", notes);
      const r = await fetch(`${API}/api/listing/staging`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "X-Admin-Token": token },
        body: fd,
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Failed (${r.status})`);
      }
      const d = await r.json();
      setStaged(d.staged_data_uri || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setFile(null); setPreview(null); setStaged(null); setError("");
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div data-testid="studio-staging-tab">
      <p className="text-slate-600">Upload an empty-room photo + pick a style. Smart engine furnishes the room while keeping walls, windows, and flooring identical. Typical generation time: 30-60s.</p>

      <div className="mt-6 grid lg:grid-cols-[360px_1fr] gap-6">
        {/* Controls */}
        <div className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Empty room photo</label>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} data-testid="studio-staging-file" className="hidden" id="studio-staging-file-input"/>
            <label htmlFor="studio-staging-file-input" className="mt-1 flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/30 rounded-xl cursor-pointer transition-colors text-center">
              <Upload className="w-6 h-6 text-slate-400"/>
              <div className="text-sm text-slate-600">{file ? <b className="text-slate-900">{file.name}</b> : "Click to upload JPEG / PNG / WEBP"}</div>
              <div className="text-[11px] text-slate-400">Max 6 MB · best results ≥ 1024px wide</div>
            </label>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Style</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {STAGE_STYLES.map((s) => (
                <button key={s.key} data-testid={`studio-staging-style-${s.key}`} onClick={() => setStyle(s.key)} className={cn("px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors", style === s.key ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-emerald-300 text-slate-700")}>{s.label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Notes (optional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. 2-bed Toronto rental, broad appeal" data-testid="studio-staging-notes" className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"/>
          </div>

          <div className="flex items-center gap-2">
            <button data-testid="studio-staging-generate" onClick={generate} disabled={!file || busy} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm rounded-lg disabled:opacity-50">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Staging…</> : <><Wand2 className="w-4 h-4"/> Stage this room</>}
            </button>
            {(file || staged) && (
              <button onClick={reset} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"><RotateCcw className="w-3.5 h-3.5"/>Reset</button>
            )}
          </div>

          {error && <div className="text-rose-700 bg-rose-50 border border-rose-200 px-4 py-3 rounded-lg text-sm">{error}</div>}
        </div>

        {/* Preview pane */}
        <div className="space-y-4">
          <PreviewPanel label="Before" src={preview} placeholder="Empty room appears here"/>
          <PreviewPanel
            label="After"
            src={staged}
            placeholder={busy ? "Generating…" : "Staged room appears here"}
            downloadName={file ? `staged-${file.name.replace(/\.[^.]+$/, "")}.png` : "staged.png"}
            busy={busy}
          />
        </div>
      </div>
    </div>
  );
}

function PreviewPanel({ label, src, placeholder, downloadName, busy }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</span>
        {src && downloadName && (
          <a href={src} download={downloadName} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"><Download className="w-3.5 h-3.5"/>Download</a>
        )}
      </div>
      {src
        ? <img src={src} alt={label} className="w-full rounded-xl border border-slate-200 shadow-sm" data-testid={`studio-staging-preview-${label.toLowerCase()}`}/>
        : (
          <div className="aspect-video bg-slate-100 border border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-400 gap-2 text-sm">
            {busy ? <Loader2 className="w-6 h-6 animate-spin"/> : <ImageIcon className="w-6 h-6"/>}
            <span>{placeholder}</span>
          </div>
        )
      }
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spec Sheet tab
// ---------------------------------------------------------------------------
const SPEC_FIELDS = [
  ["address",    "Address",      "text",    "123 Yonge St #1502", true],
  ["city",       "City",         "text",    "Toronto",            false],
  ["province",   "Province",     "text",    "Ontario",            false],
  ["beds",       "Beds",         "number",  "2",                  false],
  ["baths",      "Baths",        "number",  "1",                  false],
  ["sqft",       "Sqft",         "number",  "850",                false],
  ["rent_cad",   "Monthly rent (CAD)", "number", "2850",          false],
];
const SPEC_EXTRA = [
  ["parking",    "Parking",   "1 underground"],
  ["laundry",    "Laundry",   "in-suite"],
  ["pets",       "Pets",      "cats welcome"],
  ["utilities",  "Utilities", "heat + water included"],
];

function SpecSheetTab({ token }) {
  const [form, setForm] = useState({
    address: "", city: "", province: "", beds: 2, baths: 1, sqft: 0, rent_cad: 0,
    parking: "", laundry: "", pets: "", utilities: "", highlights: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [out, setOut] = useState(null);
  const [copied, setCopied] = useState(null);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function generate() {
    if (!form.address.trim()) { setError("Address is required"); return; }
    setBusy(true); setError(""); setOut(null);
    try {
      const body = {
        ...form,
        beds:     Number(form.beds) || 0,
        baths:    Number(form.baths) || 0,
        sqft:     Number(form.sqft) || 0,
        rent_cad: Number(form.rent_cad) || 0,
      };
      setOut(await apiJson("/spec-sheet", { token, method: "POST", body }));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  function copy(key, text) {
    navigator.clipboard.writeText(text || "").then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
    });
  }

  function copyAll() {
    if (!out) return;
    const txt = [
      out.headline,
      "",
      out.summary,
      "",
      "Highlights:",
      ...((out.highlights || []).map((h) => `• ${h}`)),
      "",
      "Neighbourhood:",
      out.neighbourhood,
      "",
      "Things to know:",
      out.things_to_know,
      "",
      out.tagline,
    ].join("\n");
    copy("all", txt);
  }

  return (
    <div data-testid="studio-spec-tab">
      <p className="text-slate-600">Fill the form → smart engine writes a polished, factual one-pager. Copy section-by-section or download the full thing.</p>

      <div className="mt-6 grid lg:grid-cols-[420px_1fr] gap-6">
        {/* Form */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          {SPEC_FIELDS.map(([k, label, type, ph, req]) => (
            <div key={k}>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}{req && <span className="text-rose-500"> *</span>}</label>
              <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} data-testid={`studio-spec-${k}`} className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"/>
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            {SPEC_EXTRA.map(([k, label, ph]) => (
              <div key={k}>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</label>
                <input value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} data-testid={`studio-spec-${k}`} className="mt-1 w-full px-2.5 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"/>
              </div>
            ))}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Highlights / context</label>
            <textarea rows={3} value={form.highlights} onChange={(e) => set("highlights", e.target.value)} placeholder="e.g. floor-to-ceiling windows, walk to Eaton Centre, newly renovated kitchen" data-testid="studio-spec-highlights" className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 resize-none"/>
          </div>
          <button data-testid="studio-spec-generate" onClick={generate} disabled={busy || !form.address.trim()} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm rounded-lg disabled:opacity-60">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Drafting…</> : <><FileText className="w-4 h-4"/> Generate spec sheet</>}
          </button>
          {error && <div className="text-rose-700 bg-rose-50 border border-rose-200 px-4 py-3 rounded-lg text-sm">{error}</div>}
        </div>

        {/* Output */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8">
          {!out
            ? <div className="text-slate-400 text-sm flex items-center gap-2"><FileText className="w-5 h-5"/>Spec sheet appears here.</div>
            : (
              <div data-testid="studio-spec-result">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-display text-2xl font-bold text-slate-900 leading-tight flex-1">{out.headline}</h2>
                  <button onClick={copyAll} className={cn("inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap", copied === "all" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 hover:bg-slate-200 text-slate-700")} data-testid="studio-spec-copyall"><Copy className="w-3.5 h-3.5"/>{copied === "all" ? "Copied" : "Copy all"}</button>
                </div>
                <Section title="Summary" text={out.summary} k="summary" copy={copy} copied={copied}/>
                <div className="mt-5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Highlights</span>
                    <button onClick={() => copy("highlights", (out.highlights || []).map((h) => `• ${h}`).join("\n"))} className={cn("inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded", copied === "highlights" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 hover:bg-slate-200 text-slate-600")}><Copy className="w-3 h-3"/>{copied === "highlights" ? "Copied" : "Copy"}</button>
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {(out.highlights || []).map((h, i) => (
                      <li key={i} className="text-sm text-slate-800 flex gap-2"><span className="text-emerald-700 mt-1">▸</span><span>{h}</span></li>
                    ))}
                  </ul>
                </div>
                <Section title="Neighbourhood" text={out.neighbourhood} k="neighbourhood" copy={copy} copied={copied}/>
                <Section title="Things to know" text={out.things_to_know} k="things_to_know" copy={copy} copied={copied}/>
                <p className="mt-6 font-display text-lg italic text-emerald-700">{out.tagline}</p>
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}

function Section({ title, text, k, copy, copied }) {
  return (
    <div className="mt-5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">{title}</span>
        <button onClick={() => copy(k, text)} className={cn("inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded", copied === k ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 hover:bg-slate-200 text-slate-600")}>
          <Copy className="w-3 h-3"/>{copied === k ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="mt-2 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{text}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
const TABS = [
  { key: "staging", label: "Virtual staging", icon: Wand2 },
  { key: "spec",    label: "Spec sheet",      icon: FileText },
];

export default function ListingStudio() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("staging");

  useEffect(() => {
    if (!token) return;
    // Lightweight health-check to invalidate stale tokens.
    fetch(`${API}/api/listing/staging/styles`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "X-Admin-Token": token } })
      .then((r) => { if (r.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(""); } });
  }, [token]);

  if (!token) {
    return <LockScreen onUnlock={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }}/>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-start justify-between gap-5 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-emerald-700"><Sparkles className="w-4 h-4"/><span className="text-xs uppercase tracking-[0.2em] font-bold">Smart Listing Studio</span></div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-tight">Marketing assets in minutes</h1>
            <p className="mt-2 text-sm text-slate-500">Virtual staging + listing copy — for every property you put on the market.</p>
          </div>
          <button data-testid="studio-logout-btn" onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} className="text-xs text-slate-400 hover:text-slate-700 hover:underline">Lock</button>
        </div>

        <div className="mt-6 flex items-center gap-1 border-b border-slate-200">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} data-testid={`studio-mainTab-${t.key}`} onClick={() => setTab(t.key)} className={cn("inline-flex items-center gap-1.5 px-4 py-3 text-sm font-semibold transition-colors -mb-px border-b-2 whitespace-nowrap", tab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800")}>
                <Icon className="w-4 h-4"/>{t.label}
              </button>
            );
          })}
          <a href="/admin/viralpost" className="ml-auto inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 px-3"><ExternalLink className="w-3.5 h-3.5"/>ViralPost</a>
          <a href="/admin/follower" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 px-3"><ExternalLink className="w-3.5 h-3.5"/>Follower</a>
        </div>

        <div className="mt-7">
          {tab === "staging" && <StagingTab  token={token}/>}
          {tab === "spec"    && <SpecSheetTab token={token}/>}
        </div>
      </div>
    </div>
  );
}
